/**
 * Kim jest gracz, zanim wejdzie do świata.
 *
 * One request behind the player's page: the account, the live world, what this
 * player owns and the cell they will land on. It is deliberately **not** part
 * of `join` — asking who you are must not put you into the world, and `join`
 * spawns somebody who has never been in it.
 */

import type { Material } from '../game/buildings/Materials';

const API = import.meta.env.VITE_API_URL ?? '';

export interface Item {
  material: Material;
  amount: number;
}

export interface Summary {
  account: {
    nickname: string;
    email: string;
    createdAt: string;
    /** Null for an account that has never been in the world. */
    lastSeen: string | null;
  };
  world: {
    id: string;
    latticeRadius: number;
    chunkCount: number;
    maxElevation: number;
    /** How many people are in the world this second. */
    online: number;
    daylight: {
      phase: number;
      /** The world clock in seconds — what the weather is a function of. */
      clock: number;
      nightShare: number;
      /**
       * Cloud cover forced from the panel, 0 to 1.
       *
       * **Absent while the weather runs on its own**, which is the normal case:
       * the server does not compute a sky, it only overrides one. What the
       * cover actually is comes out of `Weather.coverAt` on this side, from the
       * same clock and the same seed every client uses.
       */
      cover?: number;
      paused: boolean;
    };
  };
  estate: {
    buildings: { kind: number; level: number; q: number; r: number; ready: boolean }[];
    /** Everything in every building of theirs, added up by material. */
    stored: Item[];
    /** What they are carrying themselves. */
    purse: Item[];
    /**
     * Ilu ludzi mieszka we wszystkich jego osadach razem.
     *
     * Suma, bo ta strona liczy budynki rodzajami, a nie sztukami. Która osada
     * ile ma, mówi dymek nad nią i panel pola w grze.
     */
    people: number;
  };
  /** Null for somebody who has never been in this world — there is no cell yet. */
  standing: {
    q: number;
    r: number;
    elevation: number;
    biome: number | null;
    /** The biome read together with what grows on it, named by the server. */
    label: string | null;
  } | null;
}

export async function loadSummary(token: string): Promise<Summary> {
  const response = await fetch(`${API}/api/player/summary`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    // The status is carried in the message because the caller tells 401 — a
    // token to throw away — apart from everything else, which is a server that
    // has no world yet or no database and which a different password will not
    // fix. The same shape `join` refuses with.
    throw new Error(`${response.status} ${await response.text()}`);
  }

  return (await response.json()) as Summary;
}
